/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import api from 'api';
import { useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import * as S from '../../../../../Dashboard.styled';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { IconButton, InputAdornment } from '@mui/material';
import { Colors } from 'styles/variables/colors';
import DropdownItem from 'views/Dashboards-B2B/components/DropdownItem';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { programNamesForCountries } from 'utils/transformProgramsToResults';

type SelectVisaProgramDropdownProps = {
    selectedProgram: string;
    dashboard?: boolean;
    mobile?: boolean;
    handleSelectedProgram: (program: string) => void;
};

const SelectVisaProgramDropdown = ({
    selectedProgram,
    mobile,
    dashboard,
    handleSelectedProgram,
}: SelectVisaProgramDropdownProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { names: enabledCountryNames } = useEnabledCountries();
    const { t } = useTranslation();
    const [programList, setProgramList] = useState<string[]>([]);

    const [isAutocompleteOpen, setAutocompleteOpen] = useState(false);
    const [inputFocused, setInputFocused] = useState(false);

    const fetchProgramList = async () => {
        try {
            const response = await api.expertiseList.get();
            const programArray = programNamesForCountries(response.data, enabledCountryNames);
            setProgramList([t('openCase', { lng: language }), ...programArray]);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        fetchProgramList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabledCountryNames]);

    const handleOptionClick = (e: React.MouseEvent<HTMLElement>, selectedProgram: string) => {
        e.stopPropagation();
        handleSelectedProgram(selectedProgram);
        setAutocompleteOpen(false);
    };

    return (
        <S.LeftDropdown dashboard={dashboard} mobile={mobile} isActive={!!selectedProgram}>
            <Autocomplete
                open={isAutocompleteOpen}
                onOpen={() => setAutocompleteOpen(true)}
                onClose={() => setAutocompleteOpen(false)}
                sx={{
                    width: '100%',
                    marginTop: '-15px',
                    '& .MuiAutocomplete-inputRoot[class*="MuiInput-root"]': {
                        paddingRight: 0,
                    },
                }}
                value={selectedProgram}
                onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                        handleSelectedProgram(newValue);
                    }
                }}
                options={[`${t('all', { lng: language })}`, ...programList]}
                isOptionEqualToValue={(option, value) => option === value || value === ''}
                renderInput={(params) => (
                    <TextField
                        onFocus={() => setInputFocused(true)}
                        onBlur={() => setInputFocused(false)}
                        {...params}
                        label={`${t('selectService', { lng: language })}`}
                        variant="standard"
                        sx={{
                            width: '100%',
                            '& label': {
                                opacity:
                                    selectedProgram ||
                                    (inputFocused && selectedProgram === '') ||
                                    isAutocompleteOpen
                                        ? 0
                                        : 1,
                                fontFamily: 'Montserrat, sans-serif !important',
                                fontSize: '15px',
                                fontWeight: '400',
                                letterSpacing: '0.2px',
                                color: Colors.darkGray,
                            },
                            '& .MuiInput-underline:before': {
                                borderBottom: 'none',
                            },
                            '& .MuiInput-underline:after': {
                                borderBottom: 'none',
                            },
                            '&:hover .MuiInput-underline:before': {
                                borderBottom: 'none !important',
                            },
                        }}
                        slotProps={{
                            ...params.slotProps,
                            input: {
                                ...params.slotProps.input,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setAutocompleteOpen(!isAutocompleteOpen)}
                                            edge="end"
                                            tabIndex={-1}
                                            style={{ padding: '4px' }}
                                        >
                                            {isAutocompleteOpen ? (
                                                <ExpandLessIcon style={{ color: 'black' }} />
                                            ) : (
                                                <ExpandMoreIcon style={{ color: 'black' }} />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <div key={props.id} style={{ overflow: 'visible' }}>
                        <DropdownItem onClick={(e) => handleOptionClick(e, option)}>
                            {option}
                        </DropdownItem>
                    </div>
                )}
                disableClearable
                ListboxProps={{ style: { maxHeight: 275 } }}
            />
        </S.LeftDropdown>
    );
};

export default SelectVisaProgramDropdown;
